/**
 * Admin-Only Feature Visibility — FRONTEND SSOT + HOOK
 *
 * The canonical list of user-releasable analytical features lives on
 * the BACKEND at /app/backend/services/feature_visibility.py.
 * The frontend queries `/api/feature-visibility` for the current
 * effective state and uses this hook to gate UI accordingly.
 *
 * Guardrails
 * ----------
 *   • FAIL-CLOSED: on network error, unknown key, or missing key the
 *     hook reports the feature as NOT visible to users — i.e., only
 *     admins see it. This matches the backend authorization contract.
 *   • Admin always retains access; callers OR this result with
 *     `user?.is_admin`.
 *   • Do NOT hard-code visibility here — this file only supplies
 *     stable key constants + the fetch/cache. The backend registry is
 *     the source of truth.
 */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Stable feature keys — MUST match backend USER_RELEASABLE_FEATURES.
export const FEATURE_KEYS = Object.freeze({
  LIVE_CAGE_KPIS: 'live_cage_kpis',
});

/**
 * useFeatureVisibility
 *
 *   const { visible, loading, refresh, isFeatureVisible } = useFeatureVisibility();
 *   if (visible[FEATURE_KEYS.LIVE_CAGE_KPIS]) { ... }
 *
 * `visible` is `{ [key]: boolean }`. Missing keys => false (fail-closed).
 */
export function useFeatureVisibility() {
  const { token } = useAuth();
  const [visible, setVisible] = useState({});
  const [viaGrant, setViaGrant] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) {
      setVisible({}); setViaGrant({}); setLoading(false); return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(
        `${BACKEND_URL}/api/feature-visibility`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const map = (resp.data && resp.data.visible) || {};
      const grantMap = (resp.data && resp.data.via_grant) || {};
      // Coerce every value strictly to boolean — never trust wire.
      const clean = {};
      const grantClean = {};
      Object.keys(map).forEach((k) => { clean[k] = map[k] === true; });
      Object.keys(grantMap).forEach((k) => { grantClean[k] = grantMap[k] === true; });
      setVisible(clean);
      setViaGrant(grantClean);
    } catch (e) {
      // FAIL-CLOSED — never assume visible on error.
      setVisible({}); setViaGrant({});
      setError(e?.response?.data?.detail || e?.message || 'load failed');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const isFeatureVisible = useCallback(
    (key) => visible[key] === true,
    [visible]
  );

  const isViaGrant = useCallback(
    (key) => viaGrant[key] === true,
    [viaGrant]
  );

  return { visible, viaGrant, loading, error, refresh: load, isFeatureVisible, isViaGrant };
}
